import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { COVERAGE_LOOK, isFilterable } from '../model/coverage';
import type { DimensionCoverage } from '../model/coverage';

/* Coverage by quality dimension: one cell per dimension, saying whether a rule checks it, one is
 * on offer, a person has to fill one in, or no rule is possible and why.
 *
 * It exists so a reader can see the *shape* of a table's quality checks at a glance, before reading
 * a single row — and so a dimension with nothing in it is explained rather than merely absent.
 * Shared by the Suggestions screen and Auto Suggest so the two cannot describe one table's coverage
 * in two vocabularies.
 */

export function CoverageReadout({
  readout,
  selected,
  onSelect,
  compact = false,
}: {
  readonly readout: readonly DimensionCoverage[];
  /** The dimension the list is narrowed to, if any. */
  readonly selected?: string;
  /** Narrows the list to one dimension; absent where there is no list to narrow. */
  readonly onSelect?: (dimension: string) => void;
  /** Auto Suggest's tighter grid, inside a panel rather than on a page. */
  readonly compact?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <section
      className={compact ? 'lq-cov lq-cov--compact' : 'lq-cov'}
      aria-label={t('coverage.title')}
    >
      <div className="lq-cov__head">
        <h3 className="lq-cov__title">{t('coverage.title')}</h3>
        <span className="lq-cov__hint">{t('coverage.hint')}</span>
      </div>
      <ul className="lq-cov__grid">
        {readout.map((entry) => (
          <li key={entry.dimension}>
            <Cell
              entry={entry}
              pressed={selected === entry.dimension}
              onSelect={
                onSelect !== undefined && isFilterable(entry) ? onSelect : undefined
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function Cell({
  entry,
  pressed,
  onSelect,
}: {
  readonly entry: DimensionCoverage;
  readonly pressed: boolean;
  readonly onSelect: ((dimension: string) => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const look = COVERAGE_LOOK[entry.state];
  const body = (
    <>
      <span className="lq-cov__dimension">
        {t(`suggestions.filter.category.${entry.dimension}`)}
      </span>
      <span className={`lq-sg__chip lq-sg__chip--sm lq-sg__chip--${look.tone}`}>
        <span
          className={look.filled ? 'lq-sg__dot lq-sg__dot--filled' : 'lq-sg__dot'}
          aria-hidden="true"
        />
        {t(`coverage.state.${entry.state}`, { count: entry.count })}
      </span>
      <span className="lq-cov__detail">
        {entry.reason ?? t(`coverage.state.${entry.state}.note`)}
      </span>
    </>
  );
  const className = `lq-cov__cell lq-cov__cell--${entry.state}`;
  if (onSelect === undefined) return <div className={className}>{body}</div>;
  return (
    <button
      type="button"
      className={`${className} lq-cov__cell--action`}
      aria-pressed={pressed}
      title={t('coverage.filterHint')}
      onClick={() => {
        onSelect(entry.dimension);
      }}
    >
      {body}
    </button>
  );
}
